import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { enhancedCreditApi } from '../../redux/apis/endpoints/creditApi';
import { setCreditBalance } from '../../redux/slices/creditSlice';
import { BACKEND_URL } from '../../utils/config';
import ManageSubscriptionModal from './ManageSubscriptionModal';

const BRAND_TEAL = '#00b5ad';

/* ── Inline success modal shown after redirect return ── */
const SubscriptionSuccessModal = ({ creditsGranted, newBalance, onClose, kind = 'subscription' }) => {
  if (creditsGranted == null) return null;
  return ReactDOM.createPortal(
    <>
      <style>{`@keyframes subSuccessSlideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <div
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      >
        <div style={{ background: '#fff', borderRadius: 16, maxWidth: 400, width: '100%', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', animation: 'subSuccessSlideUp 0.25s ease', padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, margin: '0 auto 12px', color: '#059669' }}>
            <svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3" /><path d="M14 24l7 7 13-13" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>{kind === 'pack' ? 'Credits added' : "You're subscribed to Creator"}</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
            {creditsGranted > 0 ? `+${creditsGranted} credits added · ` : ''}Balance: {newBalance ?? '--'} credits{kind === 'subscription' ? ' · renews monthly' : ''}
          </div>
          <button
            onClick={onClose}
            style={{ marginTop: 20, padding: '10px 32px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: 9999, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Back to try-on
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

/**
 * App-root subscription handler. Lives once at the top of the app so it works on
 * ANY route (not tied to a page):
 *   - On return from Stripe Checkout (?subscription=success&session_id=…) it
 *     confirms the subscription, shows a success modal, and invalidates the
 *     Credit RTK tag so the balance updates instantly everywhere.
 *   - On ?manage=subscription (e.g. from the confirmation email) it opens the
 *     manage/cancel popup.
 */
const SubscriptionManager = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isManageOpen, setManageOpen] = useState(false);

  // Success modal state
  const [successInfo, setSuccessInfo] = useState(null); // { creditsGranted, newBalance }

  const [promoError, setPromoError] = useState(null);
  useEffect(() => {
    const sessionId = searchParams.get('promo_session');
    if (!sessionId) return;
    let active = true;
    (async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/credit-packages/confirm-promo`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, body: JSON.stringify({ sessionId }) });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error('Could not confirm your promo purchase. Refresh to retry.');
        if (!active) return;
        dispatch(setCreditBalance({ balance: result.creditBalance }));
        dispatch(enhancedCreditApi.util.invalidateTags(['Credit']));
        setSuccessInfo({ creditsGranted: result.creditsGranted, newBalance: result.creditBalance, kind: result.kind });
        const next = new URLSearchParams(searchParams); next.delete('promo_session'); setSearchParams(next, { replace: true });
      } catch (e) { if (active) setPromoError(e.message); }
    })();
    return () => { active = false; };
  }, [searchParams, setSearchParams, dispatch]);

  // Confirm-on-return → instant credit refresh + success modal
  useEffect(() => {
    if (searchParams.get('subscription') !== 'success' || !searchParams.get('session_id')) return;
    const sessionId = searchParams.get('session_id');
    (async () => {
      let confirmResult = null;
      try {
        const res = await fetch(`${BACKEND_URL}/credit-packages/confirm-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ sessionId })
        });
        confirmResult = await res.json();
      } catch (e) {
        /* webhook is the backstop */
      } finally {
        // Immediately push the new balance into Redux so every component updates
        if (confirmResult && confirmResult.creditBalance != null) {
          dispatch(setCreditBalance({ balance: confirmResult.creditBalance }));
        }
        // Also invalidate the RTK query cache as a safety net
        dispatch(enhancedCreditApi.util.invalidateTags(['Credit']));

        const next = new URLSearchParams(searchParams);
        next.delete('subscription');
        next.delete('session_id');
        setSearchParams(next, { replace: true });

        // Show success modal
        setSuccessInfo({
          creditsGranted: 120, // Creator plan grants 120
          newBalance: confirmResult && confirmResult.creditBalance != null
            ? confirmResult.creditBalance
            : null
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Open manage popup from the email link
  useEffect(() => {
    if (searchParams.get('manage') === 'subscription') setManageOpen(true);
  }, [searchParams]);

  const close = useCallback(() => {
    setManageOpen(false);
    if (searchParams.get('manage')) {
      const next = new URLSearchParams(searchParams);
      next.delete('manage');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <>
      <SubscriptionSuccessModal
        kind={successInfo?.kind}
        creditsGranted={successInfo ? successInfo.creditsGranted : null}
        newBalance={successInfo ? successInfo.newBalance : null}
        onClose={() => setSuccessInfo(null)}
      />
      {promoError && <div role="alert" style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 10001, background: 'white', padding: 20 }}>{promoError}<button onClick={() => setPromoError(null)}>Close</button></div>}
      <ManageSubscriptionModal
        isOpen={isManageOpen}
        onClose={close}
        onChanged={() => dispatch(enhancedCreditApi.util.invalidateTags(['Credit']))}
      />
    </>
  );
};

export default SubscriptionManager;
